import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRompslompAutoRuns, getRompslompLog, getRompslompLogRow, retryRompslompLogRow } from '../../../services/RompslompService';
import { apiMessage } from './rompslompUtils';
import RompslompSentAs, { useRompslompLookup } from './RompslompSentAs';

const BAD = ['needs_attention', 'in_doubt', 'created', 'pending'];

export default function RompslompLog({ reloadSending }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const [onlyBad, setOnlyBad] = useState(false);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(null);
    const [runs, setRuns] = useState([]);
    const lookup = useRompslompLookup(!!detail);

    const load = useCallback(async () => {
        try { setRows((await getRompslompLog(onlyBad)).data.rows); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [onlyBad, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { (async () => { try { setRuns((await getRompslompAutoRuns()).data.runs); } catch { setRuns([]); } })(); }, []);

    const retry = async (id) => {
        setBusy(id);
        try {
            const res = await retryRompslompLogRow(id);
            toast[res.data.result.ok ? 'success' : 'warning'](t(`rompslomp.log.status.${res.data.result.status}`));
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(null); load(); reloadSending(); }
    };
    const open = async (id) => { try { setDetail((await getRompslompLogRow(id)).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } };

    if (!rows) return <Spinner animation="border" />;
    return (
        <>
            <Form.Check type="switch" id="log-bad" className="mb-3" label={t('rompslomp.log.onlyAttention')} checked={onlyBad} onChange={(e) => setOnlyBad(e.target.checked)} />
            <Table className="rompslomp-table rompslomp-log" size="sm" data-testid="sending-log">
                <thead><tr><th className="c-date">{t('rompslomp.log.time')}</th><th className="c-main">{t('rompslomp.log.document')}</th><th className="c-status">{t('rompslomp.log.status.title')}</th><th>{t('rompslomp.log.rompslompId')}</th><th className="c-narrow">{t('rompslomp.log.attempts')}</th><th className="c-wide">{t('rompslomp.log.error')}</th><th className="c-actions" /></tr></thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan="7" className="text-center text-muted" data-label={t('rompslomp.log.time')}>{t('rompslomp.log.empty')}</td></tr>}
                    {rows.map((r) => (
                        <tr key={r.id}>
                            <td data-label={t('rompslomp.log.time')}>{(r.updated_at || '').replace('T', ' ').slice(0, 19)}</td>
                            <td className="c-main" data-label={t('rompslomp.log.document')}><strong>{r.api_reference}</strong> <span className="text-muted">{t(`rompslomp.log.types.${r.doc_type}`)}</span></td>
                            <td className="c-status" data-label={t('rompslomp.log.status.title')}><strong>{t(`rompslomp.log.status.${r.status}`)}</strong></td>
                            <td data-label={t('rompslomp.log.rompslompId')}>{r.rompslomp_id ?? '-'}</td>
                            <td data-label={t('rompslomp.log.attempts')}>{r.attempts}</td>
                            <td className="small c-wide" data-label={t('rompslomp.log.error')}>{r.last_error}</td>
                            <td className="c-actions">
                                <Button size="sm" variant="light" onClick={() => open(r.id)}>{t('rompslomp.log.details')}</Button>
                                {BAD.includes(r.status) && <Button size="sm" variant="outline-primary" disabled={busy === r.id} onClick={() => retry(r.id)}>{t('rompslomp.log.retry')}</Button>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <h5 className="mt-4">{t('rompslomp.log.auto.title')}</h5>
            <Table className="rompslomp-table rompslomp-runs" size="sm" data-testid="auto-runs">
                <thead><tr><th>{t('rompslomp.log.time')}</th><th>{t('rompslomp.log.auto.event')}</th><th>{t('rompslomp.log.auto.trigger')}</th><th>{t('rompslomp.log.auto.sent')}</th><th>{t('rompslomp.log.auto.documents')}</th><th>{t('rompslomp.log.error')}</th></tr></thead>
                <tbody>
                    {runs.length === 0 && <tr><td colSpan="6" className="text-center text-muted">{t('rompslomp.log.auto.empty')}</td></tr>}
                    {runs.map((r) => (
                        <tr key={r.id}>
                            <td data-label={t('rompslomp.log.time')}>{(r.started_at || '').replace('T', ' ').slice(0, 19)}</td>
                            <td data-label={t('rompslomp.log.auto.event')}>{t(`rompslomp.log.auto.events.${r.event}`)}{r.outcome && r.event === 'run' ? ` (${t(`rompslomp.log.auto.outcomes.${r.outcome}`)})` : ''}</td>
                            <td data-label={t('rompslomp.log.auto.trigger')}>{r.trigger ? t(`rompslomp.log.auto.triggers.${r.trigger}`) : (r.by_user || '-')}</td>
                            <td data-label={t('rompslomp.log.auto.sent')}>{r.sent}</td>
                            <td data-label={t('rompslomp.log.auto.documents')}>{(r.results || []).map((x) => x.number).join(', ') || '-'}</td>
                            <td className="small c-wide" data-label={t('rompslomp.log.error')}>{r.reason}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Modal show={!!detail} onHide={() => setDetail(null)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>{detail?.api_reference}</Modal.Title></Modal.Header>
                <Modal.Body>
                    {detail?.detail && <><h6>{t('rompslomp.log.readBack')}</h6><pre className="rompslomp-payload">{JSON.stringify(detail.detail, null, 2)}</pre></>}
                    <h6>{t('rompslomp.log.payload')}</h6><pre className="rompslomp-payload">{JSON.stringify(detail?.payload, null, 2)}</pre>
                    <RompslompSentAs payload={detail?.payload} lookup={lookup} />
                </Modal.Body>
            </Modal>
        </>
    );
}
